// The season pass: a free track for everyone and a premium track above it, both climbed with Pass XP.
//
// Shop thread, 2026-10-15 (replaces the 30 daily claims of 2026-10-14): each season has PASS.tiers tiers
// (PASS.tierXp Pass XP each). Pass XP comes from claiming daily and weekly quests and from hunting
// (one per PASS.xp.killsPerXp kills, at most PASS.xp.huntCap a UTC day), so idle time counts and nothing
// is lost by missing a day. Every tier has a free reward; buyers of the premium pass also get the premium
// reward of every tier, including the ones they reached before buying. Past the last tier a bonus chest
// opens every PASS.bonusXp.
//
// Premium is bought with diamonds for the running season only:
//   - Pass, PASS.price: premium for the season. Its tiers pay more than PASS.price in diamonds back, weighted
//     to the last tiers (Cole, 2026-10-20: the pass pays for itself only for players who finish it), so a
//     player who climbs it every season can buy the next one.
//   - Pass+, PASS.plusPrice: premium plus PASS.plusTiers tiers at once, PASS.plusXpMult Pass XP from quests and
//     hunting for the rest of the season, and the season's Pass+ chat badge and title. Premium holders upgrade
//     for the difference (PASS.plusPrice - PASS.price).
//   - one tier, PASS.tierPrice: for late buyers; never past the last tier.
// While premium runs (until the season ends) kill XP gets PASS.xpMult (it stacks with VIP and the other
// boosts the same way they stack with each other), one daily quest can be redrawn each UTC day, the name
// shows in the season's colour (unless a bought colour is worn), and the player card wears the pass frame.
//
// A season that ends with rewards reached but not claimed gives them on the trainer's next visit.
// What the pass gives is looks, time savers and XP only, all bound to the account: outfits, skins,
// titles, chat badges, seals, potions, charms and name tags (never tradeable), coins (ordinary coins since 2026-10-20), boost minutes
// and diamonds (bound like every diamond). Never odds, boss keys, Tablet Fragments, legendary rolls or
// anything the market can list.
import type { TrainerDTO } from './protocol.ts';
import { bestHunt, sealFor, potionFor } from './quests.ts';
import { killCoins } from './xp.ts';
import { WEEK_CATCH } from './weekcatch.ts';
import { PODIUM } from './leaderboards.ts';
import { FOUNDER } from './founder.ts';
import { CONFIG } from './config.ts';
import { multX } from './boosts.ts';

export const PASS = {
  /** Shop offer ids (sent as { t: 'shop', offer }). */
  offer: 'pass',
  plusOffer: 'pass-plus',
  tierOffer: 'pass-tier',
  /** Diamond price of premium for the season: the $4.99 diamond pack. */
  price: 60,
  /** Pass+: premium, PASS.plusTiers tiers at once, more Pass XP all season, and its own badge and title. */
  plusPrice: 150,
  plusTiers: 25,
  /** Pass XP from quests and hunting while Pass+ is held (hunting: a point every 4 kills, up to 500 a day). */
  plusXpMult: 1.25,
  /** One tier bought on its own. Buying tiers never pays back more than climbing them: the whole row pays less than the pass and two tiers cost. */
  tierPrice: 10,
  tiers: 50,
  /**
   * Sized with the eval bots (sim/reports/2026-10-15-pass-xp-probe-2.md at 500): quest-claiming profiles earn about
   * 7,000 Pass XP a week and the idle one about 2,800, so at 600 a tier an active player reaches tier 50 in about
   * four weeks of a five-week season and an idle one about tier 23.
   */
  tierXp: 600,
  /** Past the last tier, a bonus chest opens every this much Pass XP. */
  bonusXp: 600,
  xp: {
    /** Pass XP per daily quest claimed, and per weekly quest. */
    daily: 100,
    weekly: 300,
    /** Hunting: one Pass XP per this many kills, at most huntCap a UTC day. */
    killsPerXp: 5,
    huntCap: 400,
  },
  /** Kill XP multiplier while premium runs (a third of VIP's +25%, the size of a Guild Boost). */
  xpMult: 1.1,
  /** Daily quests that can be redrawn per UTC day while premium runs. */
  rerollsPerDay: 1,
} as const;

/** One reward. Seals and potions come as the tier the trainer would buy at their best hunt when claimed; coins as that many kills at it. */
export type PassReward =
  | { k: 'xpBoost' | 'coinBoost'; n: number }
  /** Free Aura Charm time in minutes (shared/auracharm.ts). */
  | { k: 'auraCharm'; n: number }
  | { k: 'seals' | 'potions'; n: number }
  /** Coins worth this many kills at the trainer's best hunt. Ordinary coins (no coin is bound since 2026-10-20). */
  | { k: 'coins'; kills: number }
  /** A bag item that never trades (xp-charm, name-tag). */
  | { k: 'item'; id: string; n: number }
  | { k: 'outfit'; id: string }
  | { k: 'skin'; id: string }
  | { k: 'title'; id: string }
  | { k: 'badge'; id: string }
  | { k: 'diamonds'; n: number };

export interface PassBadge { id: string; name: string; glyph: string; /** Art id when it isn't icon-badge-<id> (Pass+ badges use the season's Pass+ emblem). */ art?: string }

export interface PassTier { free: PassReward[]; prem: PassReward[] }

export interface Season {
  no: number;
  name: string;
  /** First and last-plus-one UTC days (YYYY-MM-DD, Mondays). */
  start: string;
  end: string;
  /** Pass holders' name colour this season. */
  color: string;
  badges: PassBadge[];
  /** PASS.tiers tiers, tier 1 first. */
  tiers: PassTier[];
  /** The tier whose premium reward is the season's showpiece (drawn large on the pass page). */
  hero: number;
  /** What Pass+ adds for this season: a chat badge (listed in badges too) and a title. */
  plus: { badge: string; title: string };
}

/**
 * Season 1's tiers. Premium pays 70 diamonds in all (5 on every fifth tier, 2 on tiers 2, 7, 12 ...),
 * the Frost Rune badge at once, titles at 10 and 40, the Frostfall Mantle at 20, an XP Charm at 30, a
 * Name Tag at 35 and the Frostfall Fenrisborn skin at 50. The free row pays seals, potions, coins and boosts
 * and the Rimewalker title at 50.
 */
/** What makes a season its own: the tier-1 badge, the tier 10 and 40 titles, the tier-20 outfit, the hero skin and the free tier-50 title. */
interface SeasonRewards {
  badge: string; title10: string; outfit: string; title40: string; skin: string; free50: string;
  /** Where premium's diamonds sit: 'even' (season 1: 2 or 5 every few tiers, 70 in all, paid back by about tier 43) or 'end' (from season 2: 80 in all, paid back only at tier 50). */
  diamonds: 'even' | 'end';
}

/** Back-loaded premium diamonds by tier: 15 by tier 30, 45 by tier 45, 80 at tier 50 (the price, 60, only at the end). */
const END_DIAMONDS: Record<number, number> = { 10: 5, 20: 5, 30: 5, 35: 10, 40: 10, 45: 10, 50: 35 };

/** A season's 50 tiers: the shared cycles of supplies and boosts, diamonds every few tiers, and the season's own rewards. */
function seasonTiers(r: SeasonRewards): PassTier[] {
  const special: Record<number, PassReward[]> = {
    1: [{ k: 'badge', id: r.badge }, { k: 'xpBoost', n: 360 }],
    10: [{ k: 'title', id: r.title10 }],
    20: [{ k: 'outfit', id: r.outfit }],
    30: [{ k: 'item', id: 'xp-charm', n: 1 }],
    35: [{ k: 'item', id: 'name-tag', n: 1 }],
    40: [{ k: 'title', id: r.title40 }],
    50: [{ k: 'skin', id: r.skin }],
  };
  // Sized to a trainer's own pace, so they matter at every level: coins are kills' worth at the best hunt
  // (about 1,100 kills an hour), seals and potions come at the best hunt's tier. Premium: coin tiers pay about
  // 1.5 hours of hunting, boosts run 3 hours. Free: about 25 minutes of coins, 1-hour boosts.
  const premCycle: PassReward[] = [{ k: 'xpBoost', n: 180 }, { k: 'seals', n: 120 }, { k: 'coinBoost', n: 180 }, { k: 'potions', n: 60 }, { k: 'coins', kills: 1600 }];
  const freeCycle: PassReward[] = [{ k: 'seals', n: 40 }, { k: 'potions', n: 20 }, { k: 'coins', kills: 450 }, { k: 'xpBoost', n: 60 }, { k: 'seals', n: 40 }, { k: 'coinBoost', n: 60 }];
  const tiers: PassTier[] = [];
  for (let n = 1; n <= PASS.tiers; n++) {
    const prem: PassReward[] = [];
    if (r.diamonds === 'end') { if (END_DIAMONDS[n]) prem.push({ k: 'diamonds', n: END_DIAMONDS[n] }); }
    else if (n % 5 === 0) prem.push({ k: 'diamonds', n: 5 });
    else if (n % 5 === 2) prem.push({ k: 'diamonds', n: 2 });
    prem.push(...(special[n] ?? [premCycle[n % premCycle.length]]));
    const free: PassReward[] = n === 50 ? [{ k: 'title', id: r.free50 }, { k: 'coins', kills: 4000 }]
      : n === 25 ? [{ k: 'coinBoost', n: 180 }, { k: 'coins', kills: 1500 }]
        : [freeCycle[n % freeCycle.length]];
    tiers.push({ free, prem });
  }
  return tiers;
}

export const SEASONS: Season[] = [
  {
    no: 1, name: 'Frostfall', start: '2026-10-12', end: '2026-12-02', color: '#2f6684',
    badges: [{ id: 'frost-rune', name: 'Frost Rune', glyph: '❄' }, { id: 'frost-rune-plus', name: 'Frostfall Pass+', glyph: '❄', art: 'icon-pass-plus' }],
    tiers: seasonTiers({ badge: 'frost-rune', title10: 'Frostbound', outfit: 'frostfall', title40: 'Winter Warden', skin: 'fenrisborn~frostfall', free50: 'Rimewalker', diamonds: 'even' }),
    hero: 50, plus: { badge: 'frost-rune-plus', title: 'Frost Patron' },
  },
  // Season 2 (Cole picked it 2026-10-20): Yomi's winter lantern festival, a Ninefold hero.
  {
    no: 2, name: 'Lantern Night', start: '2026-12-02', end: '2027-01-06', color: '#b8432f',
    badges: [{ id: 'paper-lantern', name: 'Paper Lantern', glyph: '✹' }, { id: 'paper-lantern-plus', name: 'Lantern Night Pass+', glyph: '✹', art: 'icon-pass-plus-lanternnight' }],
    tiers: seasonTiers({ badge: 'paper-lantern', title10: 'Lanternbearer', outfit: 'lantern-robes', title40: 'Spirit Warden', skin: 'ninefold~lanternnight', free50: 'Moonwatcher', diamonds: 'end' }),
    hero: 50, plus: { badge: 'paper-lantern-plus', title: 'Lantern Patron' },
  },
];

/** The bonus chest past the last tier: free, and what premium adds. */
export const BONUS_CHEST: PassTier = { free: [{ k: 'seals', n: 40 }, { k: 'coins', kills: 200 }], prem: [{ k: 'xpBoost', n: 60 }, { k: 'potions', n: 30 }, { k: 'coins', kills: 500 }] };

/** Every badge any season gives, by id, the Weekly Catch #1 badge (shared/weekcatch.ts), the monthly Crown (shared/leaderboards.ts) and the Founder badge (shared/founder.ts). */
export const BADGE: Record<string, PassBadge> = Object.fromEntries([...SEASONS.flatMap((s) => s.badges), WEEK_CATCH.badge, PODIUM.badge, FOUNDER.badge].map((b) => [b.id, b]));
const allRewards = (s: Season) => s.tiers.flatMap((x) => [...x.free, ...x.prem]);
/** Every title any season's tiers give. */
export const PASS_TITLES: string[] = SEASONS.flatMap((s) => [...allRewards(s).flatMap((r) => (r.k === 'title' ? [r.id] : [])), s.plus.title]);

const DAY = 86_400_000;
const dayMs = (d: string) => Date.parse(`${d}T00:00:00Z`);

/** Days in a season once launch day sets the calendar. */
export const SEASON_DAYS = 35;
let BASE_DATES = SEASONS.map((s) => [s.start, s.end] as const);
const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/**
 * Key the season calendar to launch day (the admin-set day tournaments use; null = the fixed dates above).
 * Season 1 keeps running from its start through SEASON_DAYS after launch, so launch day opens with a full
 * season; each later season follows straight on for SEASON_DAYS. Server and client call it with the same day.
 */
export function setPassLaunch(launch: number | null) {
  if (launch == null) { SEASONS.forEach((s, i) => { [s.start, s.end] = BASE_DATES[i]; }); return; }
  let end = Math.floor(launch / DAY) * DAY + SEASON_DAYS * DAY;
  SEASONS.forEach((s, i) => {
    s.start = i === 0 ? BASE_DATES[0][0] : isoDay(end);
    if (i > 0) end += SEASON_DAYS * DAY;
    s.end = isoDay(end);
  });
}

/**
 * Move the whole season calendar by `ms` (the balance harness, sim/clock.ts, opens season 1 on its own first day).
 * It moves the fixed dates setPassLaunch falls back to as well, so a later setPassLaunch(null) keeps the move.
 */
export function shiftPassCalendar(ms: number) {
  const move = (d: string) => isoDay(dayMs(d) + ms);
  BASE_DATES = BASE_DATES.map(([a, b]) => [move(a), move(b)] as const);
  SEASONS.forEach((s) => { s.start = move(s.start); s.end = move(s.end); });
}

/** The season running at `now`, or null between seasons. */
export function seasonAt(now: number): Season | null {
  return SEASONS.find((s) => now >= dayMs(s.start) && now < dayMs(s.end)) ?? null;
}
export const seasonEnds = (s: Season) => dayMs(s.end);
export const seasonByNo = (no: number | undefined) => SEASONS.find((s) => s.no === no);
/** The season the pass page shows: the running one, else the latest. */
export const passSeason = (now: number): Season => seasonAt(now) ?? SEASONS.at(-1)!;

/** The pass part of a trainer. */
export interface PassState {
  /** Premium perks (XP, redraw, name colour, frame) run until this time (0 or past: none). */
  until: number;
  /** The season the Pass XP and claims below belong to. */
  season?: number;
  /** Pass XP this season. */
  xp?: number;
  /** Seasons with premium bought. */
  premium?: number[];
  /** Seasons with Pass+ bought (always with premium). */
  plus?: number[];
  /** Tiers claimed this season (1-based), free and premium rows; and bonus chests claimed. */
  free?: number[];
  prem?: number[];
  bonus?: number;
  bonusPrem?: number;
  /** Hunting's Pass XP today: the UTC day, Pass XP from it, and kills toward the next point. */
  huntDay?: string;
  huntXp?: number;
  kills?: number;
  /** Daily quest redraws used on `rerollDay`. */
  rerollDay?: string;
  rerolls?: number;
  /** Earned from passes, boards and boxes, and kept for good. */
  titles?: string[];
  badges?: string[];
  /** The badge worn beside the name in chat, if any. */
  badge?: string | null;
  /** The old daily-claim pass (before 2026-10-15): its start, days claimed and a running total. Read only by the migration. */
  start?: number;
  got?: number[];
  days?: number;
}

type Holder = Pick<TrainerDTO, 'pass'>;

export const passActive = (t: Holder, now: number) => (t.pass?.until ?? 0) > now;
/** Whole days left, rounded down. */
export const passDaysLeft = (t: Holder, now: number) => Math.max(0, Math.floor(((t.pass?.until ?? 0) - now) / DAY));

/** Whether this trainer holds premium for the season (the running one by default). */
export function hasPremium(t: Holder, now: number, season: Season | null = seasonAt(now)): boolean {
  return !!season && !!t.pass?.premium?.includes(season.no);
}

/** Whether this trainer holds Pass+ for the season (the running one by default). */
export function hasPlus(t: Holder, now: number, season: Season | null = seasonAt(now)): boolean {
  return !!season && !!t.pass?.plus?.includes(season.no);
}

/** Pass XP this season (0 when the stored progress belongs to another season). */
export function passXp(t: Holder, now: number): number {
  const s = seasonAt(now);
  return s && t.pass?.season === s.no ? t.pass.xp ?? 0 : 0;
}

/** The tier reached (0..PASS.tiers), the XP into the next one, and bonus chests opened past the end. */
export function passLevel(xp: number): { tier: number; into: number; chests: number } {
  const cap = PASS.tiers * PASS.tierXp;
  if (xp >= cap) return { tier: PASS.tiers, into: (xp - cap) % PASS.bonusXp, chests: Math.floor((xp - cap) / PASS.bonusXp) };
  return { tier: Math.floor(xp / PASS.tierXp), into: xp % PASS.tierXp, chests: 0 };
}

/** Pass XP to the end of the last tier from here (0 once there). */
export const xpToEnd = (xp: number) => Math.max(0, PASS.tiers * PASS.tierXp - xp);
/** Tiers that can still be bought: the last tier caps it. */
export const tiersLeft = (xp: number) => Math.ceil(xpToEnd(xp) / PASS.tierXp);

/**
 * Move a trainer's pass to the running season: progress from another season is reset, and its reached but
 * unclaimed rewards are returned for the caller to give. Also moves an old daily-claim pass that is still
 * running onto premium for this season. Returns null when nothing changed.
 */
export function rollPass(t: Holder, now: number): { rewards: PassReward[]; season: Season } | null {
  const s = seasonAt(now);
  const p = t.pass;
  if (!s || !p) return null;
  let changed = false;
  // The old pass (30 days from purchase, daily claims): a running one becomes premium for the season it is in.
  if (p.start !== undefined || p.got || p.days !== undefined) {
    if (p.until > now && !(p.premium ??= []).includes(s.no)) { p.premium.push(s.no); p.until = Math.max(p.until, seasonEnds(s)); }
    delete p.start; delete p.got; delete p.days;
    changed = true;
  }
  // Premium perks run to the season's end, which moves when launch day is set.
  if (p.premium?.includes(s.no) && p.until < seasonEnds(s)) { p.until = seasonEnds(s); changed = true; }
  if (p.season === s.no) return changed ? { rewards: [], season: s } : null;
  const old = seasonByNo(p.season);
  const rewards = old ? unclaimed(t, old, p.xp ?? 0) : [];
  p.season = s.no; p.xp = 0; delete p.free; delete p.prem; delete p.bonus; delete p.bonusPrem;
  return { rewards, season: s };
}

/** What a season's progress still owes: reached tiers and chests not claimed, premium ones only with premium for it. */
function unclaimed(t: Holder, s: Season, xp: number): PassReward[] {
  const p = t.pass!, prem = !!p.premium?.includes(s.no);
  const { tier, chests } = passLevel(xp);
  const out: PassReward[] = [];
  for (let n = 1; n <= tier; n++) {
    if (!p.free?.includes(n)) out.push(...s.tiers[n - 1].free);
    if (prem && !p.prem?.includes(n)) out.push(...s.tiers[n - 1].prem);
  }
  for (let i = p.bonus ?? 0; i < chests; i++) out.push(...BONUS_CHEST.free);
  if (prem) for (let i = p.bonusPrem ?? 0; i < chests; i++) out.push(...BONUS_CHEST.prem);
  return out;
}

/** Everything claimable now in the running season, and marks it claimed. */
export function claimPass(t: Holder, now: number): PassReward[] {
  const s = seasonAt(now);
  const p = t.pass;
  if (!s || !p || p.season !== s.no) return [];
  const out = unclaimed(t, s, p.xp ?? 0);
  if (!out.length) return out;
  const { tier, chests } = passLevel(p.xp ?? 0);
  const prem = hasPremium(t, now, s);
  p.free = Array.from({ length: tier }, (_, i) => i + 1);
  if (prem) p.prem = Array.from({ length: tier }, (_, i) => i + 1);
  p.bonus = chests;
  if (prem) p.bonusPrem = chests;
  return out;
}

/** Whether anything waits to be claimed in the running season. */
export function passClaimable(t: Holder, now: number): boolean {
  const s = seasonAt(now);
  const p = t.pass;
  if (!s || !p || p.season !== s.no) return false;
  const { tier, chests } = passLevel(p.xp ?? 0);
  const prem = hasPremium(t, now, s);
  return (p.free?.length ?? 0) < tier || (prem && (p.prem?.length ?? 0) < tier) || (p.bonus ?? 0) < chests || (prem && (p.bonusPrem ?? 0) < chests);
}

/** Add Pass XP in the running season (the caller rolls the pass first). Returns the XP added. */
export function addPassXp(t: Holder, now: number, n: number): number {
  const s = seasonAt(now);
  if (!s || n <= 0) return 0;
  const p = (t.pass ??= { until: 0 });
  if (p.season !== s.no) return 0;
  p.xp = (p.xp ?? 0) + n;
  return n;
}

/** Pass XP earned by play (quests): Pass+ adds PASS.plusXpMult. Bought tiers go through addPassXp. Returns the XP added. */
export function earnPassXp(t: Holder, now: number, n: number): number {
  return addPassXp(t, now, hasPlus(t, now) ? Math.round(n * PASS.plusXpMult) : n);
}

/** One kill's share of hunting Pass XP: a point every PASS.xp.killsPerXp kills, up to the day's cap (Pass+: PASS.plusXpMult of both). */
export function passKill(t: Holder, now: number, day: string): number {
  const p = t.pass;
  if (!p || p.season !== seasonAt(now)?.no) return 0;
  if (p.huntDay !== day) { p.huntDay = day; p.huntXp = 0; p.kills = 0; }
  const plus = hasPlus(t, now);
  if ((p.huntXp ?? 0) >= (plus ? Math.round(PASS.xp.huntCap * PASS.plusXpMult) : PASS.xp.huntCap)) return 0;
  p.kills = (p.kills ?? 0) + 1;
  if (p.kills < (plus ? Math.round(PASS.xp.killsPerXp / PASS.plusXpMult) : PASS.xp.killsPerXp)) return 0;
  p.kills = 0; p.huntXp = (p.huntXp ?? 0) + 1;
  return addPassXp(t, now, 1);
}

/** Start the pass state for a trainer who has none, in the running season. */
export function ensurePass(t: Holder, now: number) {
  const s = seasonAt(now);
  const p = (t.pass ??= { until: 0 });
  if (s && p.season === undefined) { p.season = s.no; p.xp ??= 0; }
  return p;
}

/** Give premium for the running season (and perks to its end). False between seasons or when already held. */
export function grantPremium(t: Holder, now: number): boolean {
  const s = seasonAt(now);
  if (!s) return false;
  const p = ensurePass(t, now);
  if ((p.premium ??= []).includes(s.no)) return false;
  p.premium.push(s.no);
  p.until = Math.max(p.until, seasonEnds(s));
  return true;
}

/** Give Pass+ for the running season: premium if not held, and its badge and title. False when already held. */
export function grantPlus(t: Holder, now: number): boolean {
  const s = seasonAt(now);
  if (!s) return false;
  const p = ensurePass(t, now);
  if ((p.plus ??= []).includes(s.no)) return false;
  grantPremium(t, now);
  p.plus.push(s.no);
  if (!(p.titles ??= []).includes(s.plus.title)) p.titles.push(s.plus.title);
  if (!(p.badges ??= []).includes(s.plus.badge)) p.badges.push(s.plus.badge);
  return true;
}

/** Diamonds a season's premium row pays when every tier is claimed. */
export const passDiamonds = (s: Season) => s.tiers.reduce((a, x) => a + x.prem.reduce((b, r) => b + (r.k === 'diamonds' ? r.n : 0), 0), 0);

/** The bag item a seal or potion reward pays at this trainer level. */
export function passItem(r: PassReward, trainerLevel: number): string | null {
  const h = bestHunt(trainerLevel).level;
  return r.k === 'seals' ? sealFor(h) : r.k === 'potions' ? potionFor(h) : r.k === 'item' ? r.id : null;
}

/** Coins a coins reward pays at this trainer level. */
export const passCoins = (r: Extract<PassReward, { k: 'coins' }>, trainerLevel: number) => killCoins(bestHunt(trainerLevel).level) * r.kills;

/** Quest redraws left today. */
export function rerollsLeft(t: Holder, now: number, day: string): number {
  if (!passActive(t, now)) return 0;
  const p = t.pass!;
  return Math.max(0, PASS.rerollsPerDay - (p.rerollDay === day ? p.rerolls ?? 0 : 0));
}

/** Titles a trainer may wear from passes. */
export const passTitles = (t: Holder) => t.pass?.titles ?? [];

/** The colour a name shows in: a bought colour first, else the season's while premium runs. */
export function nameColorOf(t: Pick<TrainerDTO, 'pass' | 'nameColor'>, now: number): string | null {
  if (t.nameColor) return t.nameColor;
  return passActive(t, now) ? seasonAt(now)?.color ?? SEASONS.at(-1)!.color : null;
}

/** The chat badge a trainer shows: worn and earned. */
export function badgeOf(t: Holder): PassBadge | null {
  const id = t.pass?.badge;
  return id && t.pass?.badges?.includes(id) ? BADGE[id] ?? null : null;
}

/** Plain label for one reward. */
export function passRewardLabel(r: PassReward, outfitName = (id: string) => id, itemName = (id: string) => id, trainerLevel = 1, skinName = (id: string) => id): string {
  const time = (m: number) => (m % 60 ? `${m} min` : `${m / 60} h`);
  switch (r.k) {
    case 'xpBoost': return `${time(r.n)} Boost ${multX(CONFIG.xpBoostMult)}`;
    case 'coinBoost': return `${time(r.n)} Boost ${multX(CONFIG.coinBoostMult, 'coins')}`;
    case 'auraCharm': return `${time(r.n)} Aura Charm`;
    case 'seals': case 'potions': return `${r.n} ${itemName(passItem(r, trainerLevel)!)}s`;
    case 'item': return r.n === 1 ? itemName(r.id) : `${r.n} ${itemName(r.id)}s`;
    case 'coins': return `${passCoins(r, trainerLevel).toLocaleString('en-US')} coins`;
    case 'outfit': return `Outfit: ${outfitName(r.id)}`;
    case 'skin': return `Skin: ${skinName(r.id)}`;
    case 'title': return `Title: ${r.id}`;
    case 'badge': return `Chat badge: ${BADGE[r.id]?.name ?? r.id}`;
    case 'diamonds': return `${r.n} diamonds`;
  }
}
